import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useNotification } from './NotificationContext';
import { safeJson, extractError } from '../utils/httpErrors';

interface User {
  id: string;
  username: string;
  email: string;
  preferences: {
    emailNotifications: boolean;
    pushNotifications: boolean;
    eventTypes: string[];
    regions: string[];
  };
  verified: boolean;
  isAdmin?: boolean;
}

interface RefreshOptions {
  force?: boolean;
}

interface RegistrationResult {
  success: boolean;
  emailSent: boolean;
  message: string;
}

interface AccountUpdateResult {
  success: boolean;
  requiresVerification?: boolean;
  emailSent?: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  loginWithGoogle: (credential: string) => Promise<boolean>;
  register: (username: string, password: string, email?: string) => Promise<RegistrationResult>;
  logout: () => void;
  updatePreferences: (preferences: any) => Promise<boolean>;
  updateUsername: (username: string) => Promise<boolean>;
  updateEmail: (email: string, currentPassword: string) => Promise<AccountUpdateResult>;
  updatePassword: (currentPassword: string, newPassword: string) => Promise<boolean>;
  resendVerification: () => Promise<boolean>;
  refreshProfile: (options?: RefreshOptions) => Promise<void>;
  isAuthenticated: boolean;
}

const LOCAL_DEV_AUTH_TOKEN_PREFIX = 'local-dev-auth:';
const LOCAL_DEV_USER_KEY = 'tcg-calendar-local-dev-user';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const isLocalDevToken = (value: string | null) => Boolean(value?.startsWith(LOCAL_DEV_AUTH_TOKEN_PREFIX));

const canUseLocalDevAuth = (apiBase: string) => (
  process.env.NODE_ENV === 'development'
  && /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?/i.test(apiBase)
);

// Safe localStorage helpers: a denied-storage browser (private mode, policy)
// throws on the getter itself, so every access needs a try/catch.
const safeGetItem = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSetItem = (key: string, value: string): boolean => {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch {
    // Storage denied; the in-memory state still works this session.
    return false;
  }
};

const safeRemoveItem = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch {
    // Storage denied; nothing to remove.
  }
};

const clearStoredSession = (): void => {
  safeRemoveItem('token');
  safeRemoveItem(LOCAL_DEV_USER_KEY);
};

const persistSessionToken = (value: string): boolean => {
  const persisted = safeSetItem('token', value);
  // A failed replacement must not leave the previous account available on reload.
  if (!persisted) clearStoredSession();
  return persisted;
};

const persistLocalDevUser = (value: User): void => {
  if (!safeSetItem(LOCAL_DEV_USER_KEY, JSON.stringify(value))) clearStoredSession();
};

const readLocalDevUser = (): User | null => {
  try {
    const storedUser = safeGetItem(LOCAL_DEV_USER_KEY);
    return storedUser ? JSON.parse(storedUser) as User : null;
  } catch {
    safeRemoveItem(LOCAL_DEV_USER_KEY);
    return null;
  }
};

const createLocalDevUser = (identifier: string): User => {
  const trimmedIdentifier = identifier.trim();
  const isEmail = trimmedIdentifier.includes('@');
  const usernameBase = isEmail ? trimmedIdentifier.split('@')[0] : trimmedIdentifier;
  const username = usernameBase.replace(/[^a-zA-Z0-9]/g, '').slice(0, 20) || 'LocalUser';

  return {
    id: `local-dev-${username.toLowerCase()}`,
    username,
    email: isEmail ? trimmedIdentifier : `${username.toLowerCase()}@local.dev`,
    preferences: {
      emailNotifications: false,
      pushNotifications: false,
      eventTypes: [],
      regions: [],
    },
    verified: true,
    isAdmin: false,
  };
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const localDevAuthEnabled = canUseLocalDevAuth(API_BASE);
  const [token, setToken] = useState<string | null>(() => safeGetItem('token'));
  const [user, setUser] = useState<User | null>(() => (
    localDevAuthEnabled && isLocalDevToken(token) ? readLocalDevUser() : null
  ));
  const [isLoading, setIsLoading] = useState(true);
  const { showSuccess, showError, showWarning } = useNotification();

  const sessionGenerationRef = React.useRef(0);
  const lastProfileRefreshRef = React.useRef<number>(0);
  const refreshInFlightRef = React.useRef<boolean>(false);
  const refreshRequestIdRef = React.useRef(0);
  const tokenRef = React.useRef<string | null>(token);
  const devUserRef = React.useRef<User | null>(user);

  const updateSessionToken = useCallback((nextToken: string | null) => {
    tokenRef.current = nextToken;
    setToken(nextToken);
  }, []);

  const beginSessionTransition = useCallback(() => {
    sessionGenerationRef.current += 1;
    refreshRequestIdRef.current += 1;
    refreshInFlightRef.current = false;
    return sessionGenerationRef.current;
  }, []);

  const isSessionCurrent = useCallback((generation: number, expectedToken?: string | null) => (
    sessionGenerationRef.current === generation
    && (expectedToken === undefined || tokenRef.current === expectedToken)
  ), []);

  const syncVerificationFlag = useCallback((verified: boolean | undefined) => {
    if (verified) {
      safeSetItem('emailVerified', 'true');
    } else {
      safeRemoveItem('emailVerified');
    }
  }, []);

  const clearSession = useCallback(() => {
    beginSessionTransition();
    setUser(null);
    devUserRef.current = null;
    updateSessionToken(null);
    clearStoredSession();
    safeRemoveItem('emailVerified');
  }, [beginSessionTransition, updateSessionToken]);

  const applyAuthenticatedResponse = useCallback((
    data: { token?: string; user?: User },
    expectedGeneration: number,
    expectedToken?: string | null,
  ): boolean => {
    if (!isSessionCurrent(expectedGeneration, expectedToken)) return false;

    // Any token-bearing authentication response starts a fresh session
    // generation. This invalidates profile requests that were started under
    // an older account or token.
    beginSessionTransition();
    devUserRef.current = null;
    if (data.token) {
      updateSessionToken(data.token);
      persistSessionToken(data.token);
    }
    if (data.user) {
      // Authentication and credential-change responses contain a complete
      // public user. Replace the identity wholesale so fields from a previous
      // account cannot leak into the new one.
      setUser(data.user);
      syncVerificationFlag(data.user.verified);
    }
    return true;
  }, [beginSessionTransition, isSessionCurrent, syncVerificationFlag, updateSessionToken]);

  const signInLocalDev = useCallback((identifier: string): boolean => {
    const localUser = createLocalDevUser(identifier);
    const localToken = `${LOCAL_DEV_AUTH_TOKEN_PREFIX}${Date.now().toString(36)}`;

    beginSessionTransition();
    updateSessionToken(localToken);
    setUser(localUser);
    devUserRef.current = localUser;
    if (persistSessionToken(localToken)) persistLocalDevUser(localUser);
    syncVerificationFlag(true);
    showSuccess('Signed in with a local development profile.', 'Local sign in');
    return true;
  }, [beginSessionTransition, showSuccess, syncVerificationFlag, updateSessionToken]);

  useEffect(() => {
    let cancelled = false;
    let requestInFlight = false;
    let profileLoaded = false;
    let retryAttempt = 0;
    let retryTimer: number | undefined;
    const maxAutomaticRetries = 5;

    const scheduleRetry = (checkAuth: () => Promise<void>) => {
      if (cancelled || retryTimer !== undefined || retryAttempt >= maxAutomaticRetries) return;
      const delay = Math.min(30000, 1000 * (2 ** retryAttempt));
      retryAttempt += 1;
      retryTimer = window.setTimeout(() => {
        retryTimer = undefined;
        void checkAuth();
      }, delay);
    };

    const checkAuth = async () => {
      if (cancelled || requestInFlight) return;
      requestInFlight = true;
      const currentToken = tokenRef.current;
      const requestGeneration = sessionGenerationRef.current;
      try {
        if (isLocalDevToken(currentToken)) {
          if (localDevAuthEnabled && devUserRef.current) {
            if (!cancelled && isSessionCurrent(requestGeneration, currentToken)) {
              setUser(devUserRef.current);
              syncVerificationFlag(true);
              profileLoaded = true;
            }
            return;
          }

          if (isSessionCurrent(requestGeneration, currentToken)) clearSession();
          return;
        }

        if (currentToken) {
          // Add cache-busting parameter to prevent browser caching
          const timestamp = new Date().getTime();
          const response = await fetch(`${API_BASE}/api/auth/profile?_t=${timestamp}`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${currentToken}`,
              'Content-Type': 'application/json',
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache',
              'Expires': '0'
            },
          });

          if (response.ok) {
            const data = await response.json();
            if (!isSessionCurrent(requestGeneration, currentToken)) return;
            retryAttempt = 0;
            if (!cancelled) {
              setUser(data.user);
              profileLoaded = true;
              if (typeof data.user?.verified === 'boolean') {
                syncVerificationFlag(data.user.verified);
              }
            }
          } else if (response.status === 401 || response.status === 403) {
            // Only an explicit authentication rejection proves the token is
            // invalid. A temporary API or database failure must not destroy a
            // valid session in this browser.
            if (isSessionCurrent(requestGeneration, currentToken)) clearSession();
          } else {
            console.warn(`Profile refresh temporarily unavailable (HTTP ${response.status}); keeping the session token.`);
            if (isSessionCurrent(requestGeneration, currentToken)) scheduleRetry(checkAuth);
          }
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
        // Keep the token. The user may be offline or the API may be having a
        // short outage, neither condition invalidates the session.
        if (currentToken && isSessionCurrent(requestGeneration, currentToken)) scheduleRetry(checkAuth);
      } finally {
        requestInFlight = false;
        if (!cancelled) setIsLoading(false);
      }
    };

    const retryNow = () => {
      const currentToken = tokenRef.current;
      if (profileLoaded || !currentToken || isLocalDevToken(currentToken) || requestInFlight) return;
      if (retryTimer !== undefined) {
        window.clearTimeout(retryTimer);
        retryTimer = undefined;
      }
      retryAttempt = 0;
      void checkAuth();
    };

    window.addEventListener('online', retryNow);
    window.addEventListener('focus', retryNow);
    void checkAuth();
    return () => {
      cancelled = true;
      if (retryTimer !== undefined) window.clearTimeout(retryTimer);
      window.removeEventListener('online', retryNow);
      window.removeEventListener('focus', retryNow);
    };
  }, [API_BASE, clearSession, isSessionCurrent, localDevAuthEnabled, syncVerificationFlag, token]);

  const login = async (username: string, password: string): Promise<boolean> => {
    const attemptGeneration = beginSessionTransition();
    try {
      const response = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      if (response.ok) {
        const data = await response.json();
        if (!applyAuthenticatedResponse(data, attemptGeneration)) return false;
        showSuccess(`Welcome back, ${data.user.username}!`, 'Successfully signed in');
        return true;
      } else {
        const errorData = await response.json().catch(() => ({}));
        if (!isSessionCurrent(attemptGeneration)) return false;
        if (response.status === 503 && localDevAuthEnabled) {
          return signInLocalDev(username);
        }

        if (response.status === 503) {
          throw new Error(errorData.error || 'Sign in is temporarily unavailable.');
        }

        showError(errorData.error || 'Invalid credentials. Please try again.', 'Sign in failed');
        return false;
      }
    } catch (error) {
      if (!isSessionCurrent(attemptGeneration)) return false;
      if (localDevAuthEnabled) {
        return signInLocalDev(username);
      }

      const message = error instanceof Error
        ? error.message
        : 'Unable to connect to the server. Please check your connection.';
      showError(message, 'Sign in unavailable');
      throw new Error(message);
    }
  };

  // Google Identity Services credential exchange. Unlike login(), a failure
  // never falls back to the local development fake session: the server's
  // safe error (or a generic connection message) is shown instead.
  const loginWithGoogle = async (credential: string): Promise<boolean> => {
    const attemptGeneration = beginSessionTransition();
    try {
      const response = await fetch(`${API_BASE}/api/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ credential }),
      });

      const data = await response.json().catch(() => ({}));
      if (!isSessionCurrent(attemptGeneration)) return false;

      if (!response.ok) {
        showError(data.error || 'Google sign-in failed. Please try again.', 'Google sign-in failed');
        return false;
      }

      if (!applyAuthenticatedResponse(data, attemptGeneration)) return false;
      showSuccess(
        data.created
          ? `Welcome to TCG Calendar, ${data.user.username}!`
          : `Welcome back, ${data.user.username}!`,
        'Successfully signed in'
      );
      return true;
    } catch (error) {
      if (!isSessionCurrent(attemptGeneration)) return false;
      showError('Unable to connect to the server. Please check your connection.', 'Google sign-in unavailable');
      return false;
    }
  };

  const register = async (username: string, password: string, email?: string): Promise<RegistrationResult> => {
    const attemptGeneration = beginSessionTransition();
    try {
      const response = await fetch(`${API_BASE}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password, email }),
      });

      if (response.ok) {
        const data = await response.json();
        if (!applyAuthenticatedResponse(data, attemptGeneration)) {
          return { success: false, emailSent: false, message: 'A newer sign-in request replaced this registration.' };
        }
        const emailSent = data.emailSent !== false;
        if (emailSent) {
          showSuccess(`Welcome to TCG Calendar, ${data.user.username}!`, 'Account created successfully');
        } else {
          showWarning(data.message, 'Account created, email delayed');
        }
        return { success: true, emailSent, message: data.message };
      } else {
        const errorData = await safeJson(response);
        if (!isSessionCurrent(attemptGeneration)) {
          return { success: false, emailSent: false, message: 'A newer sign-in request replaced this registration.' };
        }
        const errorMsg = extractError(errorData, 'Registration failed. Please try again.');
        showError(errorMsg, 'Registration error');
        return { success: false, emailSent: false, message: extractError(errorData, 'Registration failed.') };
      }
    } catch (error) {
      if (!isSessionCurrent(attemptGeneration)) {
        return { success: false, emailSent: false, message: 'A newer sign-in request replaced this registration.' };
      }
      showError('Unable to connect to the server. Please check your connection.', 'Connection error');
      return { success: false, emailSent: false, message: 'Unable to connect to the server.' };
    }
  };

  const logout = () => {
    const username = user?.username;
    clearSession();
    showSuccess(username ? `Goodbye, ${username}! See you next time.` : 'You have been signed out.', 'Successfully signed out');
  };

  const updateEmail = async (email: string, currentPassword: string): Promise<AccountUpdateResult> => {
    if (isLocalDevToken(tokenRef.current)) {
      const updatedUser = user ? { ...user, email, verified: true } : null;
      if (updatedUser) {
        setUser(updatedUser);
        devUserRef.current = updatedUser;
        persistLocalDevUser(updatedUser);
      }
      showSuccess('Your local email address has been updated.', 'Email updated');
      return { success: true, requiresVerification: false, emailSent: false };
    }

    const requestToken = tokenRef.current;
    const requestGeneration = sessionGenerationRef.current;
    if (!requestToken) return { success: false };

    try {
      const response = await fetch(`${API_BASE}/api/auth/email`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${requestToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, currentPassword }),
      });
      if (!response.ok) {
        const errorData = await safeJson(response);
        if (!isSessionCurrent(requestGeneration, requestToken)) return { success: false };
        showError(extractError(errorData, 'Failed to update your email address.'), 'Email update failed');
        return { success: false };
      }

      const data = await response.json().catch(() => ({}));
      if (!isSessionCurrent(requestGeneration, requestToken)) return { success: false };
      if (!applyAuthenticatedResponse(data, requestGeneration, requestToken)) return { success: false };
      const emailSent = data.emailSent !== false;
      if (emailSent) {
        showSuccess('Your email address was updated. Check the new address for a verification link.', 'Verify your new email');
      } else {
        showWarning('Your email address was updated, but the verification email is delayed. You can resend it from Settings.', 'Verification email delayed');
      }
      return {
        success: true,
        requiresVerification: data.requiresVerification === true,
        emailSent,
      };
    } catch (error) {
      showError('Unable to connect to the server. Your email address was not changed.', 'Connection error');
      return { success: false };
    }
  };

  const updatePassword = async (currentPassword: string, newPassword: string): Promise<boolean> => {
    if (isLocalDevToken(tokenRef.current)) {
      showSuccess('Your local development password does not need to be changed.', 'Local profile');
      return true;
    }

    const requestToken = tokenRef.current;
    const requestGeneration = sessionGenerationRef.current;
    if (!requestToken) return false;

    try {
      const response = await fetch(`${API_BASE}/api/auth/password`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${requestToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      if (!response.ok) {
        const errorData = await safeJson(response);
        if (!isSessionCurrent(requestGeneration, requestToken)) return false;
        showError(extractError(errorData, 'Failed to update your password.'), 'Update failed');
        return false;
      }

      const data = await response.json().catch(() => ({}));
      if (!isSessionCurrent(requestGeneration, requestToken)) return false;
      if (!applyAuthenticatedResponse(data, requestGeneration, requestToken)) return false;
      showSuccess('Your password has been updated successfully.', 'Password updated');
      return true;
    } catch (error) {
      showError('Unable to connect to the server. Your password was not changed.', 'Connection error');
      return false;
    }
  };

  const updatePreferences = async (preferences: any): Promise<boolean> => {
    if (isLocalDevToken(tokenRef.current)) {
      const updatedUser = user ? { ...user, preferences: { ...user.preferences, ...preferences } } : null;
      if (updatedUser) {
        setUser(updatedUser);
        devUserRef.current = updatedUser;
        persistLocalDevUser(updatedUser);
      }
      showSuccess('Your local notification preferences have been updated.', 'Preferences saved');
      return true;
    }

    const requestToken = tokenRef.current;
    const requestGeneration = sessionGenerationRef.current;
    if (!requestToken) return false;

    try {
      const response = await fetch(`${API_BASE}/api/auth/preferences`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${requestToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ preferences }),
      });

      if (response.ok) {
        const data = await response.json();
        if (!isSessionCurrent(requestGeneration, requestToken)) return false;
        setUser(data.user);
        showSuccess('Your notification preferences have been updated.', 'Preferences saved');
        if (typeof data.user?.verified === 'boolean') {
          syncVerificationFlag(data.user.verified);
        }
        return true;
      } else {
        const errorData = await safeJson(response);
        if (!isSessionCurrent(requestGeneration, requestToken)) return false;
        showError(extractError(errorData, 'Failed to update preferences. Please try again.'), 'Update failed');
        return false;
      }
    } catch (error) {
      showError('Unable to connect to the server. Please check your connection.', 'Connection error');
      return false;
    }
  };

  const updateUsername = async (username: string): Promise<boolean> => {
    if (isLocalDevToken(tokenRef.current)) {
      const updatedUser = user ? { ...user, username } : null;
      if (updatedUser) {
        setUser(updatedUser);
        devUserRef.current = updatedUser;
        persistLocalDevUser(updatedUser);
      }
      showSuccess('Your local username has been updated successfully.', 'Username updated');
      return true;
    }

    const requestToken = tokenRef.current;
    const requestGeneration = sessionGenerationRef.current;
    if (!requestToken) return false;

    try {
      const response = await fetch(`${API_BASE}/api/auth/username`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${requestToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username }),
      });

      if (response.ok) {
        const data = await response.json();
        if (!isSessionCurrent(requestGeneration, requestToken)) return false;
        setUser(data.user);
        showSuccess('Your username has been updated successfully.', 'Username updated');
        if (typeof data.user?.verified === 'boolean') {
          syncVerificationFlag(data.user.verified);
        }
        return true;
      } else {
        const errorData = await safeJson(response);
        if (!isSessionCurrent(requestGeneration, requestToken)) return false;
        showError(extractError(errorData, 'Failed to update username. Please try again.'), 'Update failed');
        return false;
      }
    } catch (error) {
      showError('Unable to connect to the server. Please check your connection.', 'Connection error');
      return false;
    }
  };

  const resendVerification = async (): Promise<boolean> => {
    if (isLocalDevToken(tokenRef.current)) {
      showSuccess('Local development profiles are already verified.', 'Already verified');
      return true;
    }

    const requestToken = tokenRef.current;
    const requestGeneration = sessionGenerationRef.current;
    if (!requestToken) return false;

    try {
      const response = await fetch(`${API_BASE}/api/auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${requestToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!isSessionCurrent(requestGeneration, requestToken)) return false;
      if (response.ok) {
        showSuccess('Verification email sent! Please check your inbox and spam folder.', 'Email sent');
        return true;
      } else {
        const data = await response.json();
        if (data.error === 'Email is already verified') {
          showSuccess('Your email is already verified! No action needed.', 'Already verified');
            // Refresh user profile to update the verified status
            await refreshProfile({ force: true });
          return true;
        } else {
          showError(data.error || 'Failed to send verification email. Please try again.', 'Send failed');
          return false;
        }
      }
    } catch (error) {
      showError('Unable to connect to the server. Please check your connection.', 'Connection error');
      return false;
    }
  };

  const refreshProfile = useCallback(async (options: RefreshOptions = {}): Promise<void> => {
    if (!tokenRef.current) return;
    const requestToken = tokenRef.current;
    const requestGeneration = sessionGenerationRef.current;
    if (isLocalDevToken(tokenRef.current)) {
      if (localDevAuthEnabled && devUserRef.current) {
        if (isSessionCurrent(requestGeneration, requestToken)) {
          setUser(devUserRef.current);
          syncVerificationFlag(true);
        }
      }
      return;
    }

    const now = Date.now();
    const cooldown = 5000; // 5 seconds

    // Forced refreshes bypass the cooldown, but never overlap. Overlapping
    // profile responses can otherwise apply out of order.
    if (refreshInFlightRef.current) return;
    if (!options.force) {
      if (now - lastProfileRefreshRef.current < cooldown) {
        return;
      }
    }

    refreshInFlightRef.current = true;
    const refreshRequestId = ++refreshRequestIdRef.current;
    
    try {
      // Add cache-busting parameter to prevent browser caching
      const timestamp = new Date().getTime();
      const response = await fetch(`${API_BASE}/api/auth/profile?_t=${timestamp}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${requestToken}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (!isSessionCurrent(requestGeneration, requestToken)) return;
        setUser(data.user);
        if (typeof data.user?.verified === 'boolean') {
          syncVerificationFlag(data.user.verified);
        }
        console.log('🔄 Profile refreshed:', data.user.verified ? 'VERIFIED' : 'NOT VERIFIED');
      } else if (response.status === 401 || response.status === 403) {
        if (isSessionCurrent(requestGeneration, requestToken)) clearSession();
      }
    } catch (error) {
      console.error('Error refreshing user profile:', error);
    } finally {
      if (refreshRequestIdRef.current === refreshRequestId) {
        lastProfileRefreshRef.current = Date.now();
        refreshInFlightRef.current = false;
      }
    }
  }, [API_BASE, clearSession, isSessionCurrent, localDevAuthEnabled, syncVerificationFlag]);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === 'token') {
        beginSessionTransition();
        setUser(null);
        devUserRef.current = localDevAuthEnabled && isLocalDevToken(event.newValue) ? readLocalDevUser() : null;
        updateSessionToken(event.newValue);
        setIsLoading(Boolean(event.newValue));
        if (!event.newValue) syncVerificationFlag(false);
        return;
      }

      if (event.key === 'emailVerified' && event.newValue === 'true' && tokenRef.current) {
        refreshProfile({ force: true });
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  }, [beginSessionTransition, localDevAuthEnabled, refreshProfile, syncVerificationFlag, updateSessionToken]);

  const value: AuthContextType = {
    user,
    token,
    isLoading,
    login,
    loginWithGoogle,
    register,
    logout,
    updatePreferences,
    updateUsername,
    updateEmail,
    updatePassword,
    resendVerification,
    refreshProfile,
    isAuthenticated: Boolean(user && token),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
